<template>
  <v-container>
    <v-card class="home-filters px-3 rounded-xl" elevation="0" light>
      <v-row no-gutters class="justify-space-between">
        <v-col cols="12" md="3">
          <div class="d-flex home-filters-block mb-2 mb-md-0 my-4 my-md-0 pa-2">
            <div class="flex home-filters-item">
              <p class="text-caption grey--text mb-2">{{ $t("location") }}</p>
              <v-autocomplete
                flat
                attach="#my-element"
                v-model="pickerValue"
                :items="pickerItems"
                item-text="name"
                item-value="id"
                hide-no-data
                hide-details
                dense
                rounded
                clearable
                :placeholder="getPlaceholderText()"
                prepend-inner-icon="mdi-map-marker-outline"
              >
                <template v-slot:item="data">
                  <div
                    :class="[
                      'dropdown-item mx-0',
                      {
                        'dropdown-item--selected':
                          data.attrs && data.attrs['aria-selected'] === 'true',
                      },
                    ]"
                  >
                    <v-icon class="dropdown-icon" small
                      >mdi-map-marker-outline</v-icon
                    >
                    <div>
                      <div class="dropdown-city">
                        {{
                          displayCity(data.item.name) ||
                          JSON.stringify(data.item)
                        }}
                      </div>
                      <div class="dropdown-country">
                        {{ data.item.country }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
              <div
                id="my-element"
                class="p-absolute"
                style="top: 70px"
                :class="{ 'rtl-my-element': isRTL }"
              ></div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="3">
          <div class="d-block d-md-flex">
            <v-divider class="d-none d-md-block mx-2 my-3" vertical />
            <site-date-picker :value.sync="dateRange" is-range is-custom>
              <div class="d-flex home-filters-block mb-2 mb-md-0 pa-2">
                <div
                  class="flex home-filters-item"
                  :class="{ 'rtl-date-item': $i18n.locale === 'he' }"
                >
                  <p class="text-caption grey--text mb-2">
                    {{
                      isMobile
                        ? $t("checkin")
                        : $t("checkin") + " / " + $t("checkout")
                    }}
                  </p>
                  <div
                    class="d-flex align-center date-range-container"
                    :class="{ 'rtl-date-container': $i18n.locale === 'he' }"
                  >
                    <v-icon
                      small
                      :class="$i18n.locale === 'he' ? 'ml-2' : 'mr-2'"
                      >mdi-calendar-blank-outline</v-icon
                    >
                    <div v-if="dateRange.startDate">
                      <div
                        class="d-md-none"
                        v-text="parseDate(dateRange.startDate, 'MMM DD')"
                      />
                      <div class="d-none d-md-block">
                        <span
                          v-text="parseDate(dateRange.startDate, 'MMM DD')"
                        />
                        &mdash;
                        <span v-text="parseDate(dateRange.endDate, 'MMM DD')" />
                      </div>
                    </div>
                    <span class="grey--text" v-else>{{
                      $t("select_date")
                    }}</span>
                  </div>
                </div>
                <v-divider class="d-md-none mx-4" vertical />
                <div
                  class="flex home-filters-item d-md-none"
                  :class="{ 'rtl-date-item': $i18n.locale === 'he' }"
                >
                  <p class="text-caption grey--text mb-2">
                    {{ $t("checkout") }}
                  </p>
                  <div
                    class="d-flex align-center date-range-container"
                    :class="{ 'rtl-date-container': $i18n.locale === 'he' }"
                  >
                    <v-icon
                      small
                      :class="$i18n.locale === 'he' ? 'ml-2' : 'mr-2'"
                      >mdi-calendar-blank-outline</v-icon
                    >
                    <div
                      v-if="dateRange.endDate"
                      v-text="parseDate(dateRange.endDate, 'MMM DD')"
                    />
                    <span class="grey--text" v-else>{{
                      $t("select_date")
                    }}</span>
                  </div>
                </div>
                <!-- <v-btn
                v-if="dateRange.startDate"
                class="ml-2"
                icon
                small
                @click="clear"
              >
                <v-icon small> fa fa-times </v-icon>
              </v-btn> -->
              </div>
            </site-date-picker>
          </div>
        </v-col>
        <v-col
          cols="12"
          md="3"
          class="d-flex align-center justify-space-between"
        >
          <div
            class="d-flex mb-2 mb-md-0 pa-0 pa-md-2 date-filters"
            :class="{
              'home-filters-block': !isMobile,
              'gap-2': isMobile,
              'rtl-guests-section': $i18n.locale === 'he',
            }"
          >
            <v-divider class="d-none d-md-block mr-4" vertical />
            <div
              class="flex home-filters-item mr-md-0 justify-space-around"
              :class="{
                'home-filters-block flex-row align-center': isMobile,
                'rtl-guest-item': $i18n.locale === 'he',
              }"
            >
              <p class="text-caption text-sm-caption grey--text mb-0">
                {{ $t("adults") }}
              </p>
              <v-divider v-if="isMobile" vertical />
              <count-picker :value.sync="adults" />
            </div>
            <v-divider v-if="!isMobile" class="mx-4" vertical />
            <div
              class="flex home-filters-item justify-space-around"
              :class="{
                'home-filters-block flex-row align-center': isMobile,
                'rtl-guest-item': $i18n.locale === 'he',
              }"
            >
              <p class="text-caption text-sm-caption grey--text mb-0">
                {{ $t("children") }}
              </p>
              <v-divider v-if="isMobile" vertical />
              <count-picker :value.sync="children" :min="0" />
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            :color="actionsColor"
            dark
            elevation="0"
            height="50px"
            block
            class="rounded-lg my-4"
            @click="search"
          >
            <v-icon small>$search</v-icon>
            <span class="text-capitalize ml-3">{{ $t("search") }}</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import FormattersMixin from "@/mixins/formatters-mixin";
import SiteDatePicker from "@/components/common/site-date-picker";
import CountPicker from "@/components/common/count-picker.vue";
import DeviceMixin from "@/mixins/device-mixin";
import { mapState } from "vuex";
import stylesMixin from "@/mixins/styles-mixin";
import { SEANRENT_CITIES } from "@/consts/consts";

export default {
  name: "home-filters",
  components: { SiteDatePicker, CountPicker },
  mixins: [FormattersMixin, DeviceMixin, stylesMixin],
  data: () => ({
    city: null,
    region: null,
    neighborhood: null,
    dateRange: {
      startDate: null,
      endDate: null,
    },
    adults: 1,
    children: 0,
  }),
  mounted() {
    // this.dateRange.startDate = this.$moment()
    //   .add(1, "day")
    //   .format("YYYY-MM-DD");
    // this.dateRange.endDate = this.$moment().add(3, "days").format("YYYY-MM-DD");
    // Debug log for first city name
    if (!this.isHotelSite) return;
    this.dateRange.startDate = this.$moment()
      .add(1, "day")
      .format("YYYY-MM-DD");
    this.dateRange.endDate = this.$moment().add(2, "day").format("YYYY-MM-DD");
    if (this.cities && this.cities.length > 0) {
      console.log("First city name raw:", this.cities[0].name);
    }
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    isHotelSite() {
      return this.settings.site_type === "hotel" && !this.isMobile;
    },
    currentFilterType() {
      if (this.settings.filter_by_neighborhood) {
        return "neighborhood";
      } else if (this.settings.filter_by_region) {
        return "region";
      } else {
        return "city";
      }
    },
    cities() {
      const raw =
        this.settings.name === "seanrentt"
          ? SEANRENT_CITIES
          : this.settings.cities;
      if (raw?.length && typeof raw[0] === "object") return raw;
      return (raw || []).map((city) => ({ name: city, id: city }));
    },
    pickerItems() {
      if (this.currentFilterType === "neighborhood") {
        return this.settings.neighborhoods || [];
      } else if (this.currentFilterType === "region") {
        return this.settings.regions || [];
      } else {
        return this.cities;
      }
    },
    pickerValue: {
      get() {
        if (this.currentFilterType === "neighborhood") {
          return this.neighborhood;
        } else if (this.currentFilterType === "region") {
          return this.region;
        } else {
          return this.city;
        }
      },
      set(val) {
        // Reset all location values first
        this.city = null;
        this.region = null;
        this.neighborhood = null;

        // Set the appropriate value based on current filter type
        if (this.currentFilterType === "neighborhood") {
          this.neighborhood = val;
        } else if (this.currentFilterType === "region") {
          this.region = val;
        } else {
          this.city = val;
        }
      },
    },
    displayCity() {
      return (name) => name || "";
    },
  },
  watch: {
    "$route.query"() {
      this.dateRange = {
        startDate: this.$route.query.check_in,
        endDate: this.$route.query.check_out,
      };
    },
    "settings.filter_by_region"(val) {
      this.clearLocationData();
    },
    "settings.filter_by_neighborhood"(val) {
      this.clearLocationData();
    },
  },
  methods: {
    search() {
      const { startDate, endDate } = this.dateRange;
      const check_in =
        startDate && this.$moment(startDate).format("YYYY-MM-DD");
      const check_out = endDate && this.$moment(endDate).format("YYYY-MM-DD");

      const routeLocation = {
        name: "listings",
        query: {
          check_in,
          check_out,
          city: this.city,
          region: this.region,
          neighborhood: this.neighborhood,
          adults: this.adults,
          children: this.children,
          lang: this.$i18n.locale,
        },
      };

      if (window.location.pathname.includes("/search-widget")) {
        const resolvedRoute = this.$router.resolve(routeLocation);
        window.open(resolvedRoute.href, "_blank");
      } else {
        this.$router.push(routeLocation);
      }
    },
    clear() {
      this.dateRange = { startDate: null, endDate: null };
    },
    getPlaceholderText() {
      if (this.currentFilterType === "neighborhood") {
        return this.$t("select_neighborhood");
      } else if (this.currentFilterType === "region") {
        return this.$t("select_region");
      } else {
        return this.$t("select_city");
      }
    },
    clearLocationData() {
      this.city = null;
      this.region = null;
      this.neighborhood = null;
    },
  },
};
</script>

<style scoped lang="scss">
.home-filters {
  &-block {
    border-radius: 5px;
    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      border: 1px solid map-get($grey, "lighten-3");
    }
  }
}

// RTL support for date pickers
.rtl-date-item {
  direction: rtl;
  text-align: right;

  .text-caption {
    text-align: right;
  }
}

.rtl-date-container {
  direction: rtl;
  flex-direction: row-reverse;

  .v-icon {
    order: 1;
  }

  > div,
  > span {
    order: 2;
    text-align: right;
  }
}

// RTL support for guest sections
.rtl-guests-section {
  direction: rtl;

  .v-divider--vertical {
    order: -1;
  }
}

.rtl-guest-item {
  direction: rtl;
  text-align: right;

  .text-caption {
    text-align: right;
  }

  .count-picker {
    direction: ltr; // Keep the actual counter in LTR for usability
  }
}

::v-deep .v-text-field--rounded > .v-input__control > .v-input__slot {
  padding: 0 !important;
}
.date-range-container {
  white-space: nowrap;
}
.date-filters {
  width: 100%;
}
.dropdown-header {
  font-weight: 400;
  font-size: 1rem;
  padding: 8px 16px 4px 16px;
  color: #6b7280;
}
.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-radius: 12px;
  margin: 0 8px 4px 8px;
  font-size: 0.92rem;
}
.dropdown-icon {
  margin-right: 12px;
  color: #23272a;
}
.dropdown-city {
  font-weight: 400;
  font-size: 0.98rem;
  color: #374151;
}
.dropdown-country {
  font-size: 0.85rem;
  color: #a0aec0;
  font-weight: 300;
}
::v-deep .v-menu__content {
  margin-top: -10px !important;
  border-radius: 20px !important;
  z-index: 3000 !important;
  max-height: 187px !important;
  overflow-y: auto !important;
}
.home-filters-block {
  display: flex;
  align-items: stretch;
}
.home-filters-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 56px;
  min-height: 56px;
}
::v-deep .count-picker {
  height: 100%;
  display: flex;
  align-items: center;
}
.dropdown-item--selected {
  width: 100%;
}
.rtl-my-element {
  left: 0;
  top: 0 !important;
  position: relative;
}
</style>
